import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Box,
  TextField,
  Button,
  Alert,
  Typography,
  Container,
  IconButton,
  Paper,
  CircularProgress,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { useNavigate, useLocation } from "react-router-dom";
import gridoIcon from '../images/grido.jpg';

// Importación de tus configuraciones
import { 
  API_BASE_URL, 
  LOGIN, 
  TIEMPOEXPIRACIONCOOKIEENMINUTOS,
} from '../services/apiConfig';

function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  // El interceptor global de axios (index.js) redirige acá con este query
  // param cuando el modo mantenimiento corta una sesión activa — mismo
  // mensaje fijo que ya usa el backend cuando bloquea el login en sí.
  const [error, setError] = useState(() =>
    new URLSearchParams(location.search).get('mantenimiento')
      ? 'Estamos realizando mejoras, por favor intente de nuevo en unos minutos.'
      : ""
  );
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [colorSistema, setColorSistema] = useState(() => localStorage.getItem('color_sistema') || '#071c44');
  const [nombreSistema, setNombreSistema] = useState(() => localStorage.getItem('nombre_sistema') || 'Sistema de Gestión');

  const handleEmailChange = (event) => {
    setEmail(event.target.value);
  };

  const handlePasswordChange = (event) => {
    setPassword(event.target.value);
  };

  const handleShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    if (submitting) return;
    setError("");
    setSubmitting(true);
    axios
      .post(API_BASE_URL + LOGIN, {
        email: email,
        password: password,
      })
      .then((response) => {
        if (response.data.status === "success") {
          // 1. Guardamos estado básico
          sessionStorage.setItem("loggedIn", "true");
          
          // 2. Guardamos la info del usuario
          sessionStorage.setItem("userData", JSON.stringify(response.data.data));
          
          // 3. Guardamos el JWT
          const token = response.data.data.token;
          sessionStorage.setItem("authToken", token);

          // 4. Calculamos expiración para control interno
          const maxAge = TIEMPOEXPIRACIONCOOKIEENMINUTOS * 60;
          const expiryTime = new Date().getTime() + maxAge * 1000;
          sessionStorage.setItem("expiryTime", expiryTime);

          navigate("/registro-producto?mode=create");
        } else {
          setError(response.data.message);
          setSubmitting(false);
        }
      })
      .catch((err) => {
        setError("Error de conexión con el servidor");
        setSubmitting(false);
      });
  };

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        minHeight: "100vh",
        backgroundColor: "#f4f6f8", // Color de fondo sutil
      }}
    >
      <Container maxWidth="xs">
        <Paper
          elevation={3}
          sx={{
            p: isMobile ? 3 : 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            borderRadius: 2
          }}
        >
          {/* Ícono arriba y título centrado debajo: con nombres de sistema
              largos el título puede envolver a 2 líneas — así el ícono nunca
              queda mal alineado contra un bloque de texto de más de 1 línea. */}
          <Box
            component="img"
            src={gridoIcon}
            alt="Grido"
            sx={{ height: isMobile ? 36 : 44, width: 'auto', mb: 1, borderRadius: 1 }}
          />
          <Typography
            sx={{
                mb: 3,
                textAlign: 'center',
                fontWeight: 'bold',
                color: colorSistema
            }}
            variant={isMobile ? 'h5' : 'h4'}
            component="h1"
          >
            {nombreSistema}
          </Typography>

          {error && (
            <Alert sx={{ mb: 2, width: '100%' }} severity="error">
              {error}
            </Alert>
          )}

          <form onSubmit={handleSubmit} style={{ width: '100%' }}>
            <TextField
              type="email"
              value={email}
              onChange={handleEmailChange}
              variant="outlined"
              id="email"
              label="Correo Electrónico"
              autoComplete="username"
              autoFocus
              disabled={submitting}
              fullWidth
              required
              sx={{ mb: 2 }}
            />

            <TextField
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={handlePasswordChange}
              variant="outlined"
              id="password"
              label="Contraseña"
              autoComplete="current-password"
              disabled={submitting}
              fullWidth
              required
              sx={{ mb: 3 }}
              InputProps={{
                endAdornment: (
                  <IconButton onClick={handleShowPassword} edge="end" disabled={submitting}>
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                ),
              }}
            />

            <Button
              variant="contained"
              type="submit"
              fullWidth
              size="large"
              disabled={submitting}
              startIcon={submitting ? <CircularProgress size={20} color="inherit" /> : null}
              sx={{
                backgroundColor: colorSistema,
                py: 1.5,
                fontWeight: 'bold',
                '&:hover': {
                  backgroundColor: colorSistema,
                  filter: 'brightness(0.9)'
                },
              }}
            >
              {submitting ? 'Ingresando…' : 'Iniciar Sesión'}
            </Button>
          </form>

          <Typography variant="body2" color="textSecondary" sx={{ mt: 3 }}>
            © {new Date().getFullYear()} Agilia Locales
          </Typography>
        </Paper>
      </Container>
    </Box>
  );
}

export default Login;